import { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  useTheme,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  People as PeopleIcon,
  Store as StoreIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Category as CategoryIcon,
  Inventory as InventoryIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useGetUsersQuery } from '../store/services/users';
import { useGetShopsQuery } from '../store/services/shops';
import { useGetProductsQuery } from '../store/services/products';
import { useGetProductCategoriesQuery } from '../store/services/productCategories';
import { useGetCompaniesQuery } from '../store/services/companies';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
  isLoading?: boolean;
}

function StatCard({ title, value, icon, color, subtitle, isLoading }: StatCardProps) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            {isLoading ? (
              <CircularProgress size={24} />
            ) : (
              <Typography variant="h4">{value}</Typography>
            )}
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: color,
              borderRadius: '50%',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function RecentActivityCard({ title, items, isLoading }: { title: string; items: any[]; isLoading?: boolean }) {
  const sortedItems = items ? [...items].sort((a, b) => 
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  ) : [];

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List>
            {sortedItems.slice(0, 5).map((item, index) => (
              <Box key={item._id || index}>
                <ListItem disablePadding>
                  <ListItemText
                    primary={item.name}
                    secondary={new Date(item.created_at).toLocaleDateString()}
                  />
                </ListItem>
                {index < Math.min(sortedItems.length - 1, 4) && <Divider />}
              </Box>
            ))}
            {sortedItems.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                No items to display
              </Typography>
            )}
          </List>
        )}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const theme = useTheme();
  const { data: users, isLoading: isLoadingUsers } = useGetUsersQuery();
  const { data: shops, isLoading: isLoadingShops } = useGetShopsQuery();
  const { data: products, isLoading: isLoadingProducts } = useGetProductsQuery();
  const { data: productCategories, isLoading: isLoadingCategories } = useGetProductCategoriesQuery();
  const { data: companies, isLoading: isLoadingCompanies } = useGetCompaniesQuery();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalShops: 0,
    totalShopOwners: 0,
    totalShopEmployees: 0,
    totalProducts: 0,
    totalCategories: 0,
    totalCompanies: 0,
  });

  useEffect(() => {
    if (users && shops && products && productCategories && companies) {
      setStats({
        totalUsers: users.length,
        totalShops: shops.length,
        totalShopOwners: users.filter((user: any) => user.role === 'ShopOwner').length,
        totalShopEmployees: users.filter((user: any) => user.role === 'ShopEmployee').length,
        totalProducts: products.length,
        totalCategories: productCategories.length,
        totalCompanies: companies.length,
      });
    }
  }, [users, shops, products, productCategories, companies]);

  if (currentUser?.role === 'ShopEmployee') {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Welcome to OmniServe Admin
        </Typography>
        <Typography variant="body1" gutterBottom>
          You are logged in as a Shop Employee. You can view shop details and manage products.
        </Typography>
        {isLoadingShops ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : shops && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom>
              My Shop Details
            </Typography>
            <Paper sx={{ p: 3 }}>
              {shops
                ?.filter((shop: any) => shop._id === currentUser.shop_id)
                .map((shop: any) => (
                  <Box key={shop._id}>
                    <Typography variant="h6">{shop.name}</Typography>
                    <Typography variant="body1">Address: {shop.address}</Typography>
                    <Typography variant="body1">Contact: {shop.contact_number}</Typography>
                  </Box>
                ))}
            </Paper>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard Overview
      </Typography>
      
      {/* Main Stats */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            icon={<PeopleIcon sx={{ color: '#fff' }} />}
            color={theme.palette.primary.main}
            isLoading={isLoadingUsers}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Shops"
            value={stats.totalShops}
            icon={<StoreIcon sx={{ color: '#fff' }} />}
            color={theme.palette.secondary.main}
            isLoading={isLoadingShops}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Products"
            value={stats.totalProducts}
            icon={<InventoryIcon sx={{ color: '#fff' }} />}
            color={theme.palette.success.main}
            isLoading={isLoadingProducts}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Companies"
            value={stats.totalCompanies}
            icon={<BusinessIcon sx={{ color: '#fff' }} />}
            color={theme.palette.info.main}
            isLoading={isLoadingCompanies}
          />
        </Grid>
      </Grid>

      {/* Secondary Stats */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Product Categories"
            value={stats.totalCategories}
            icon={<CategoryIcon sx={{ color: '#fff' }} />}
            color={theme.palette.warning.main}
            isLoading={isLoadingCategories}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Shop Owners"
            value={stats.totalShopOwners}
            icon={<PersonIcon sx={{ color: '#fff' }} />}
            color={theme.palette.error.main}
            isLoading={isLoadingUsers}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Shop Employees"
            value={stats.totalShopEmployees}
            icon={<GroupIcon sx={{ color: '#fff' }} />}
            color={theme.palette.grey[700]}
            isLoading={isLoadingUsers}
          />
        </Grid>
      </Grid>

      {/* Recent Activity Section */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={4}>
          <RecentActivityCard
            title="Recent Products"
            items={products || []}
            isLoading={isLoadingProducts}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <RecentActivityCard
            title="Recent Companies"
            items={companies || []}
            isLoading={isLoadingCompanies}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <RecentActivityCard
            title="Recent Shops"
            items={shops || []}
            isLoading={isLoadingShops}
          />
        </Grid>
      </Grid>

      {/* Shop Owner Specific Section */}
      {currentUser?.role === 'ShopOwner' && shops && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            My Shop Details
          </Typography>
          <Paper sx={{ p: 3 }}>
            {shops
              ?.filter((shop: any) => shop._id === currentUser.shop_id)
              .map((shop: any) => (
                <Box key={shop._id}>
                  <Typography variant="h6">{shop.name}</Typography>
                  <Typography variant="body1">Address: {shop.address}</Typography>
                  <Typography variant="body1">Contact: {shop.contact_number}</Typography>
                  <Typography variant="body1">
                    Employees:{' '}
                    {users?.filter(
                      (user: any) =>
                        user.shop_id === shop._id && user.role === 'ShopEmployee'
                    ).length || 0}
                  </Typography>
                </Box>
              ))}
          </Paper>
        </Box>
      )}
    </Box>
  );
} 